import {
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    Avatar,
    Badge,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    DocsTable,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    CheckIcon,
    StarIcon
} from "../../anatomy/icons";

const NAME = "Avatar";

const USAGE_CODE = `<Avatar
    statusIndicatorType="online"
    title="Ada Lovelace"
    isStatusIndicator={true}
    size="large"
/>

<Avatar
    accessibilityLabel="Open profile menu, Ada Lovelace"
    onPress={handleOpenProfileMenu}
    isWorkWithAction={true}
    title="Ada Lovelace"
    type="info"
/>`;

const CONTENT_CODE = `<Avatar
    imageUrl="https://example.com/ada.png"
    title="Ada Lovelace"
/>`;

const BADGE_CODE = `const VerifiedIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <CheckIcon
    color={customColor}
    size={size}
/>;

<Avatar
    badge={{
        accessibilityLabel: "Verified account",
        icon: VerifiedIcon,
        type: "primary"
    }}
    title="Ada Lovelace"
/>

<Badge.Indicator
    location={{
        bottom: 0,
        end: 0
    }}
    count={3}
>
    <Avatar
        title="Ada Lovelace"
    />
</Badge.Indicator>`;

const AVATAR_IMAGE_SVG = "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 96 96'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='#7ea3ff'/><stop offset='1' stop-color='#4b5fd6'/></linearGradient></defs><rect width='96' height='96' fill='url(#g)'/><circle cx='48' cy='38' r='16' fill='#ffffff' fill-opacity='0.92'/><path d='M14 96c2-20 16-30 34-30s32 10 34 30z' fill='#ffffff' fill-opacity='0.92'/></svg>";

const AVATAR_IMAGE_URL = `data:image/svg+xml,${encodeURIComponent(AVATAR_IMAGE_SVG)}`;

const SIZES = [
    "xxSmall",
    "xSmall",
    "small",
    "medium",
    "large",
    "xLarge"
] as const;

const TYPES = [
    "neutral",
    "primary",
    "success",
    "info",
    "warning",
    "danger"
] as const;

const STATUSES = [
    "online",
    "away",
    "busy",
    "idle",
    "offline"
] as const;

const BADGE_SIZES = [
    "xSmall",
    "small",
    "medium",
    "large"
] as const;

const BADGE_STATUSES = [
    "online",
    "busy",
    "away"
] as const;

const BADGE_STATUS_KEYS = {
    online: "avatarPage.badgeOnline",
    busy: "avatarPage.badgeBusy",
    away: "avatarPage.badgeAway"
} as const;

const BADGE_COUNT = 3;

const ABBREVIATIONS = [
    "first-last",
    "first-next",
    "every-first"
] as const;

const LOADING_DELAY = 1200;

const CELL_STYLE = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex"
} as const;

const SLOT_STYLE = {
    justifyContent: "center",
    alignItems: "center",
    display: "flex"
} as const;

const SampleCell = ({
    isLarge = false,
    children,
    label
}: {
    children: ReactNode;
    isLarge?: boolean;
    label: string;
}) => {
    const {
        avatarSizes,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <div
        style={{
            ...CELL_STYLE,
            gap: spaces.spacingMd
        }}
    >
        <div
            style={{
                ...SLOT_STYLE,
                height: isLarge ? avatarSizes.xLarge : avatarSizes.large
            }}
        >
            {children}
        </div>
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("avatarPage.contentTitle"),
        id: "content"
    },
    {
        title: t("avatarPage.typesTitle"),
        id: "types"
    },
    {
        title: t("avatarPage.statusTitle"),
        id: "status"
    },
    {
        title: t("avatarPage.badgeTitle"),
        id: "badge"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const AvatarPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        avatarSizes
    } = NCoreUIKitTheme.useContext();

    const [
        statusIndex,
        setStatusIndex
    ] = useState(0);

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const status = STATUSES[statusIndex] ?? "online";

    useEffect(() => {
        if(!isLoading) {
            return;
        }

        const timer = window.setTimeout(() => setIsLoading(false), LOADING_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isLoading
    ]);

    const handleCycleStatus = () => {
        setStatusIndex((statusIndex + 1) % STATUSES.length);
    };

    return <Article>
        <ComponentHeader
            lead={t("avatarPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("avatarPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("avatarPage.overviewCaption")}
            >
                <SampleCell
                    label={`statusIndicatorType="${status}"`}
                    isLarge={true}
                >
                    <Avatar
                        statusIndicatorType={status}
                        onPress={handleCycleStatus}
                        title={t("sample.name")}
                        isStatusIndicator={true}
                        isWorkWithAction={true}
                        size="large"
                    />
                </SampleCell>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("avatarPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <SampleCell
                        label={`${size} · ${avatarSizes[size]}`}
                        isLarge={true}
                        key={size}
                    >
                        <Avatar
                            title={t("sample.name")}
                            isStatusIndicator={true}
                            size={size}
                        />
                    </SampleCell>)
                }
            </Preview>
        </Section>
        <Section
            title={t("avatarPage.contentTitle")}
            id="content"
        >
            <Paragraph>
                {t("avatarPage.contentText")}
            </Paragraph>
            <Preview>
                <SampleCell
                    label="imageUrl"
                >
                    <Avatar
                        imageUrl={AVATAR_IMAGE_URL}
                        size="large"
                    />
                </SampleCell>
                <SampleCell
                    label="title"
                >
                    <Avatar
                        title={t("sample.name")}
                        size="large"
                    />
                </SampleCell>
                <SampleCell
                    label="icon"
                >
                    <Avatar
                        icon={StarIcon}
                        size="large"
                    />
                </SampleCell>
                <SampleCell
                    label={t("avatarPage.contentDefault")}
                >
                    <Avatar
                        size="large"
                    />
                </SampleCell>
            </Preview>
            <CodeBlock
                code={CONTENT_CODE}
                language="tsx"
            />
            <Paragraph>
                {t("avatarPage.abbreviationText")}
            </Paragraph>
            <Preview>
                {
                    ABBREVIATIONS.map((abbreviation) => <SampleCell
                        label={abbreviation}
                        key={abbreviation}
                    >
                        <Avatar
                            titleAbbreviationType={abbreviation}
                            title={t("avatarPage.longName")}
                            size="large"
                        />
                    </SampleCell>)
                }
            </Preview>
        </Section>
        <Section
            title={t("avatarPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("avatarPage.typesText")}
            </Paragraph>
            <Preview>
                {
                    TYPES.map((type) => <SampleCell
                        label={type}
                        key={type}
                    >
                        <Avatar
                            title={t("sample.name")}
                            size="large"
                            type={type}
                        />
                    </SampleCell>)
                }
            </Preview>
        </Section>
        <Section
            title={t("avatarPage.statusTitle")}
            id="status"
        >
            <Paragraph>
                {t("avatarPage.statusText")}
            </Paragraph>
            <Preview>
                {
                    STATUSES.map((statusType) => <SampleCell
                        label={statusType}
                        key={statusType}
                    >
                        <Avatar
                            statusIndicatorType={statusType}
                            title={t("sample.name")}
                            isStatusIndicator={true}
                            size="large"
                        />
                    </SampleCell>)
                }
            </Preview>
        </Section>
        <Section
            title={t("avatarPage.badgeTitle")}
            id="badge"
        >
            <Paragraph>
                {t("avatarPage.badgeText")}
            </Paragraph>
            <DocsTable
                headers={[
                    t("avatarPage.badgeSize"),
                    ...BADGE_STATUSES.map((statusType) => t(BADGE_STATUS_KEYS[statusType]))
                ]}
                rows={
                    BADGE_SIZES.map((size) => [
                        avatarSizes[size],
                        ...BADGE_STATUSES.map((statusType) => <Avatar
                            statusIndicatorType={statusType}
                            title={t("sample.name")}
                            isStatusIndicator={true}
                            key={statusType}
                            size={size}
                        />)
                    ])
                }
            />
            <DocsTable
                rows={
                    BADGE_SIZES.map((size) => [
                        avatarSizes[size],
                        <Avatar
                            badge={{
                                accessibilityLabel: t("avatarPage.badgeVerifiedLabel"),
                                icon: CheckIcon
                            }}
                            title={t("sample.name")}
                            key="verified"
                            size={size}
                        />,
                        <Badge.Indicator
                            count={BADGE_COUNT}
                            location={{
                                bottom: 0,
                                end: 0
                            }}
                            key="count"
                        >
                            <Avatar
                                title={t("sample.name")}
                                size={size}
                            />
                        </Badge.Indicator>
                    ])
                }
                headers={[
                    t("avatarPage.badgeSize"),
                    t("avatarPage.badgeVerified"),
                    t("avatarPage.badgeCount")
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("avatarPage.badgeCaption")}
            </Paragraph>
            <CodeBlock
                code={BADGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("avatarPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("avatarPage.statesCaption")}
            >
                <SampleCell
                    label="isLoading"
                >
                    <Avatar
                        onPress={() => setIsLoading(true)}
                        title={t("sample.name")}
                        isWorkWithAction={true}
                        isLoading={isLoading}
                        size="large"
                    />
                </SampleCell>
                <SampleCell
                    label="isDisabled"
                >
                    <Avatar
                        onPress={() => undefined}
                        title={t("sample.name")}
                        isWorkWithAction={true}
                        isDisabled={true}
                        size="large"
                    />
                </SampleCell>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("avatarPage.a11yLabel"),
                    t("avatarPage.a11yStatus"),
                    t("avatarPage.a11yBadge"),
                    t("avatarPage.a11yLoading"),
                    t("avatarPage.a11yActionable"),
                    t("avatarPage.a11yHitArea"),
                    t("avatarPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("avatarPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("avatarPage.propImageUrl"),
                        name: "imageUrl",
                        type: "string"
                    },
                    {
                        description: t("avatarPage.propImageSource"),
                        type: "ImageURISource",
                        name: "imageSource"
                    },
                    {
                        description: t("avatarPage.propImage"),
                        type: "ComponentType<ImageProps>",
                        name: "image"
                    },
                    {
                        description: t("avatarPage.propImageProps"),
                        type: "ImageProps",
                        name: "imageProps"
                    },
                    {
                        description: t("avatarPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        type: "\"xxSmall\" | \"xSmall\" | \"small\" | \"medium\" | \"large\" | \"xLarge\"",
                        description: t("avatarPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        type: "\"primary\" | \"neutral\" | \"success\" | \"info\" | \"warning\" | \"danger\"",
                        description: t("avatarPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "keyof ContainerContentColors | \"transparent\"",
                        description: t("avatarPage.propBackgroundColor"),
                        name: "backgroundColor"
                    },
                    {
                        description: t("avatarPage.propTitleColor"),
                        type: "keyof TextContentColors",
                        name: "titleColor"
                    },
                    {
                        description: t("avatarPage.propIconColor"),
                        type: "keyof ProjectColorPalette | string",
                        name: "iconColor"
                    },
                    {
                        type: "\"first-last\" | \"first-next\" | \"every-first\"",
                        description: t("avatarPage.propTitleAbbreviationType"),
                        defaultValue: "\"first-last\"",
                        name: "titleAbbreviationType"
                    },
                    {
                        description: t("avatarPage.propIsStatusIndicator"),
                        name: "isStatusIndicator",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "\"online\" | \"offline\" | \"busy\" | \"away\" | \"idle\"",
                        description: t("avatarPage.propStatusIndicatorType"),
                        name: "statusIndicatorType",
                        defaultValue: "\"online\""
                    },
                    {
                        type: "{ icon: NCoreUIKitIcon; type?: StateColorType; accessibilityLabel?: string }",
                        description: t("avatarPage.propBadge"),
                        name: "badge"
                    },
                    {
                        description: t("avatarPage.propIsShowStatusIndicatorBorder"),
                        name: "isShowStatusIndicatorBorder",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("avatarPage.propIsShowBorder"),
                        defaultValue: "false",
                        name: "isShowBorder",
                        type: "boolean"
                    },
                    {
                        type: "keyof BorderContentColors | \"transparent\"",
                        description: t("avatarPage.propBorderColor"),
                        name: "borderColor"
                    },
                    {
                        description: t("avatarPage.propAvatarBackgroundColor"),
                        type: "keyof ContainerContentColors",
                        name: "avatarBackgroundColor",
                        defaultValue: "\"default\""
                    },
                    {
                        description: t("avatarPage.propIsWorkWithAction"),
                        name: "isWorkWithAction",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "(event: GestureResponderEvent) => void",
                        description: t("avatarPage.propOnPress"),
                        name: "onPress"
                    },
                    {
                        description: t("avatarPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("avatarPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("avatarPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("avatarPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("avatarPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("avatarPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default AvatarPage;
